// ================================================================
// 查詢歷史頁面
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { NoteAlt as NoteAltIcon, Edit as EditIcon } from '@mui/icons-material';
import { getQueryHistory, QueryHistory, formatDateTime } from '../api/qa';
import { getCategoryColor, getDynamicPeriodLabel } from '../api/questions';
import { useCurrency } from '../contexts/CurrencyContext';
import { getPairDetail } from '../api/pair';
import {
  createNote,
  updateNote,
  getNotes,
  Note,
  MOOD_OPTIONS,
} from '../api/notes';

const applyPeriodToHistoryText = (text: string): string => {
  const now = new Date();
  const { label: monthLabel } = getDynamicPeriodLabel('monthly', now);
  const { label: weekLabel } = getDynamicPeriodLabel('weekly', now);
  return text
    .replace(/下個月|本月|未來一個月/g, monthLabel)
    .replace(/本週|下週|未來七天/g, weekLabel);
};

interface NoteDialogState {
  open: boolean;
  query_id: number;
  question_text: string;
  existing_note: Note | null;
}

const QueryHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  const [queries, setQueries] = useState<QueryHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // notes: query_id -> Note
  const [noteMap, setNoteMap] = useState<Record<number, Note>>({});

  // dialog state
  const [dialog, setDialog] = useState<NoteDialogState>({
    open: false,
    query_id: 0,
    question_text: '',
    existing_note: null,
  });
  const [draftMood, setDraftMood] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [saving, setSaving] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getQueryHistory(50);
      if (result.success) {
        setQueries(result.queries);
      } else {
        setError('載入歷史失敗');
      }
    } catch (err: any) {
      setError(err.message || '載入歷史失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNotes = useCallback(async () => {
    try {
      const res = await getNotes(100);
      if (res.success) {
        const map: Record<number, Note> = {};
        res.notes.forEach(n => { map[n.query_id] = n; });
        setNoteMap(map);
      }
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadNotes();
  }, [loadHistory, loadNotes]);

  const handleViewResult = async (query: QueryHistory) => {
    if (query.question_id.startsWith('PAIR') || query.question_id === 'SIMPLE001') {
      try {
        const pairDetail = await getPairDetail(query.query_id) as any;
        const isMultiPair = pairDetail.is_multi_pair === true && Array.isArray(pairDetail.results);
        if (isMultiPair) {
          const results = pairDetail.results.map((item: any) => ({
            partner: item.candidate_name || '未知候選人',
            result: {
              success: true,
              output: item.output,
              result: item.result,
              question_id: pairDetail.question_id,
              question_text: pairDetail.question_text,
              category_id: pairDetail.category_id,
            }
          }));
          navigate('/pair/result', {
            state: { results, isMultiPair: true, personAName: pairDetail.person_a_info?.name || '本人' }
          });
        } else {
          navigate('/pair/result', {
            state: { result: pairDetail, isMultiPair: false, personAName: '本人' }
          });
        }
      } catch {
        navigate(`/answer/${query.query_id}`);
      }
    } else {
      navigate(`/answer/${query.query_id}`);
    }
  };

  const openNoteDialog = (query: QueryHistory) => {
    const existing = noteMap[query.query_id] || null;
    setDialog({
      open: true,
      query_id: query.query_id,
      question_text: query.question_text,
      existing_note: existing,
    });
    setDraftMood(existing?.mood || null);
    setDraftText(existing?.note_text || '');
  };

  const handleSaveNote = async () => {
    if (!draftText.trim() && !draftMood) return;
    setSaving(true);
    try {
      if (dialog.existing_note) {
        await updateNote(dialog.existing_note.note_id, draftMood, draftText.trim());
      } else {
        await createNote(dialog.query_id, draftMood, draftText.trim());
      }
      await loadNotes();
      setDialog(d => ({ ...d, open: false }));
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
        查詢歷史
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        查看您過去的查詢記錄，並可為每次查詢新增心得備忘錄
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : queries.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            尚無查詢記錄
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/questions')}>
            開始查詢
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>查詢時間</strong></TableCell>
                <TableCell><strong>問題</strong></TableCell>
                <TableCell><strong>費用</strong></TableCell>
                <TableCell><strong>操作</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queries.map((query) => {
                const existingNote = noteMap[query.query_id];
                return (
                  <TableRow key={query.query_id} hover>
                    <TableCell>{formatDateTime(query.created_at)}</TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={query.question_id.substring(0, query.question_id.length - 3)}
                          size="small"
                          sx={{
                            backgroundColor: getCategoryColor(query.question_id.substring(0, query.question_id.length - 3) as any),
                            color: 'white',
                            mr: 1,
                            mb: 0.5
                          }}
                        />
                        <Typography variant="body2">
                          {applyPeriodToHistoryText(query.question_text)}
                        </Typography>
                        {existingNote && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.3, fontStyle: 'italic' }}
                          >
                            {existingNote.mood
                              ? `${MOOD_OPTIONS.find(m => m.value === existingNote.mood)?.emoji || ''} `
                              : ''}
                            {existingNote.note_text}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={query.cost === 0 ? 'success.main' : 'text.primary'}>
                        {query.cost === 0 ? '免費' : formatPrice(query.cost)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewResult(query)}
                        >
                          查看結果
                        </Button>
                        <Button
                          size="small"
                          variant={existingNote ? 'contained' : 'outlined'}
                          color={existingNote ? 'secondary' : 'inherit'}
                          startIcon={existingNote ? <EditIcon fontSize="small" /> : <NoteAltIcon fontSize="small" />}
                          onClick={() => openNoteDialog(query)}
                          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                          {existingNote ? '編輯備忘' : '寫備忘'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 備忘錄對話框 */}
      <Dialog open={dialog.open} onClose={() => setDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.existing_note ? '編輯備忘錄' : '新增備忘錄'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {applyPeriodToHistoryText(dialog.question_text)}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            當時心情
          </Typography>
          <ToggleButtonGroup
            value={draftMood}
            exclusive
            onChange={(_, v) => setDraftMood(v)}
            size="small"
            sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}
          >
            {MOOD_OPTIONS.map(m => (
              <ToggleButton key={m.value} value={m.value} sx={{ px: 1.5 }}>
                {m.emoji} {m.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            label="心得備忘"
            multiline
            rows={3}
            fullWidth
            value={draftText}
            onChange={e => setDraftText(e.target.value)}
            placeholder="記錄你對這次分析的想法、感受，或你的人生大事..."
            inputProps={{ maxLength: 500 }}
            helperText={`${draftText.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(d => ({ ...d, open: false }))}>取消</Button>
          <Button
            variant="contained"
            onClick={handleSaveNote}
            disabled={saving || (!draftText.trim() && !draftMood)}
          >
            {saving ? '儲存中...' : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QueryHistoryPage;
